import { IHandle } from "@koralabs/handles-public-api-interfaces";
import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount, AssetRatio } from "@sundaeswap/asset";
import { UseQueryResult } from "@tanstack/react-query";
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ADA_METADATA } from "../../constants/cardano.constants";
import { Status, TasteTest, Transaction } from "../../gql/generated/taste-test.sdk";
import { useTasteTestHistory } from "../../gql/hooks/taste-test.query";
import { usePersistedTasteTestContributionsHistory } from "../../hooks/taste-test/usePersistedTasteTestContributions";
import {
  IUseTasteTestPersonalization,
  useTasteTestPersonalization,
} from "../../hooks/taste-test/useTasteTestPersonalization";
import { OrderActions, useOrderContext } from "../../stores/OrderContext";
import { IAssetMetaData } from "../../types/Asset.types";

export const TRENDING_TASTE_TEST_THRESHOLD = 100_000_000_000n; // Lovelaces --> 100k ADA

export interface IContributionOutcome {
  givenGatheredValue: AssetAmount;
  givenSuppliedValue: AssetAmount;
  poolShare: number;
  ratio: AssetRatio;
}

export type TActionType = "deposit" | "withdraw" | "update";

export interface ITasteTestLocalStorageContributions {
  createdAt?: number; // Timestamp
  details?: {
    assetId?: string;
    amount?: string; // Formatted Amount
  };
  expiresAt?: number; // Timestamp
  id?: string;
  txHash?: string;
  type?: TActionType;
}

export interface ITasteTestDetailContext {
  actionType: TActionType;
  activeTasteTest?: TasteTest;
  contributionOutcome?: IContributionOutcome;
  handleData?: IHandle;
  isLoading?: boolean;
  isTasteTestClosing?: boolean;
  isTasteTestDraft?: boolean;
  isTasteTestGeofenced?: boolean;
  isTasteTestInactive?: boolean;
  isTasteTestModerated?: boolean;
  isTasteTestPending?: boolean;
  isTasteTestReadOnly?: boolean;
  latestHistoryRecord?: Transaction;
  lpTokens?: number;
  persistedContributionsHistory?: ITasteTestLocalStorageContributions[];
  removePersistedContributionsHistory?: () => void;
  resetTasteTestState: (removePersistedContributions?: boolean) => void;
  setActionType: Dispatch<SetStateAction<TActionType>>;
  setShowCurrentPosition: Dispatch<SetStateAction<boolean>>;
  showCurrentPosition: boolean;
  tasteTestPersonalization?: IUseTasteTestPersonalization;
  updatePersistedContributionsHistory?: Dispatch<
    SetStateAction<ITasteTestLocalStorageContributions[]>
  >;
  withCurrentPosition: boolean;
}

const defaultTasteTestDetailState: ITasteTestDetailContext = {
  actionType: "deposit",
  activeTasteTest: undefined,
  contributionOutcome: undefined,
  handleData: undefined,
  isLoading: false,
  isTasteTestClosing: false,
  isTasteTestDraft: false,
  isTasteTestGeofenced: false,
  isTasteTestInactive: false,
  isTasteTestModerated: false,
  isTasteTestPending: false,
  isTasteTestReadOnly: false,
  latestHistoryRecord: undefined,
  lpTokens: undefined,
  persistedContributionsHistory: undefined,
  removePersistedContributionsHistory: () => {},
  resetTasteTestState: () => {},
  setActionType: () => {},
  setShowCurrentPosition: () => {},
  showCurrentPosition: false,
  tasteTestPersonalization: undefined,
  updatePersistedContributionsHistory: () => {},
  withCurrentPosition: false,
};

const TasteTestDetailContext = createContext(defaultTasteTestDetailState);

export const useActiveTasteTestDetailContext = () => useContext(TasteTestDetailContext);

interface ITasteTestDetailContextProviderProps {
  children: ReactNode;
  tasteTestByPolicyIdQuery: UseQueryResult<TasteTest>;
}

export const TasteTestDetailContextProvider: FC<ITasteTestDetailContextProviderProps> = ({
  children,
  tasteTestByPolicyIdQuery,
}) => {
  const {
    persistedContributionsHistory,
    removePersistedContributionsHistory,
    updatePersistedContributionsHistory,
  } = usePersistedTasteTestContributionsHistory();
  const [actionType, setActionType] = useState<TActionType>("deposit");
  const [showCurrentPosition, setShowCurrentPosition] = useState(true);
  const {
    dispatch,
    state: {
      assets: { given },
      flowData: { flowState },
    },
  } = useOrderContext();
  const { data: activeTasteTest, isLoading } = tasteTestByPolicyIdQuery;
  const { isLoading: isLoadingTasteTestPersonalization, tasteTestPersonalization } =
    useTasteTestPersonalization(activeTasteTest);
  const { details } = useWalletDetails();
  const { data: tasteTestHistory, isLoading: isLoadingTasteTestHistory } = useTasteTestHistory(
    details.stakingAddress,
    activeTasteTest?.policyId
  );

  const [contributionOutcome, setContributionOutcome] = useState<IContributionOutcome>();
  const [lpTokens, setLpTokens] = useState<number>();

  const isTasteTestInactive = [Status.Concluded, Status.Closing].includes(
    activeTasteTest?.status ?? ("" as Status)
  );
  const isTasteTestClosing = activeTasteTest?.status === Status.Closing;
  const isTasteTestDraft = activeTasteTest?.status === Status.Draft;
  const isTasteTestGeofenced = activeTasteTest?.status === Status.Geofenced;
  const isTasteTestModerated = activeTasteTest?.status === Status.Moderated;
  const isTasteTestPending = activeTasteTest?.status === Status.Pending;
  const isTasteTestReadOnly = [
    Status.Draft,
    Status.Pending,
    Status.Geofenced,
    Status.Moderated,
  ].includes(activeTasteTest?.status ?? ("" as Status));

  // We're taking the last transaction from the history, which is the one that has the final accumulative values
  const latestHistoryRecord = tasteTestHistory?.[tasteTestHistory.length - 1];
  const amountDeposited = useMemo(
    () =>
      new AssetAmount(
        latestHistoryRecord?.depositCumulative[0].amount,
        latestHistoryRecord?.depositCumulative[0].asset as unknown as IAssetMetaData
      ),
    [latestHistoryRecord?.depositCumulative]
  );

  const withCurrentPosition =
    !!(latestHistoryRecord && latestHistoryRecord.depositCumulative[0].amount !== "0") &&
    !!amountDeposited;

  /**
   * Reset the order flow state and remove the persisted contributions history.
   * @returns {void}
   */
  const resetTasteTestState = useCallback(() => {
    removePersistedContributionsHistory();
    OrderActions.resetOrderFlow(dispatch, "TasteTestOrderView.orderSuccess.onAction");
    setShowCurrentPosition(true);
    setActionType("deposit");
    OrderActions.setOrderConsent({ checked: false }, dispatch);
  }, [
    dispatch,
    flowState,
    removePersistedContributionsHistory,
    setActionType,
    setShowCurrentPosition,
  ]);

  useEffect(() => {
    if (!activeTasteTest || !activeTasteTest?.currentBalance || !given) {
      return;
    }

    if (!activeTasteTest.currentBalance || given.amount === 0n) {
      setContributionOutcome(undefined);
      return;
    }
    const {
      currentBalance: { tokenSupplied, tokenGathered },
    } = activeTasteTest;

    const tokenSuppliedAmount = new AssetAmount(tokenSupplied.amount, tokenSupplied.asset);
    const tokenGatheredAmount = new AssetAmount(tokenGathered.amount, tokenGathered.asset);

    let userDepositCumulative = given;

    if (latestHistoryRecord) {
      const latestHistoryRecordAmount = new AssetAmount(
        latestHistoryRecord.depositCumulative[0].amount,
        ADA_METADATA
      );
      userDepositCumulative = userDepositCumulative.add(latestHistoryRecordAmount);
    }

    const lpTokens = tokenSuppliedAmount.value
      .multiply(userDepositCumulative.value)
      .divide(tokenGatheredAmount.add(userDepositCumulative).value)
      .toNumber();

    const poolShare = userDepositCumulative.value
      .divide(tokenGatheredAmount.value.add(userDepositCumulative.value).multiply(2))
      .toNumber();

    const ratio = new AssetRatio(
      tokenGatheredAmount.add(userDepositCumulative),
      tokenSuppliedAmount
    );

    const givenSuppliedValue = new AssetAmount(
      userDepositCumulative.amount / 2n,
      userDepositCumulative.metadata.decimals
    );
    const givenGatheredValue = AssetAmount.fromValue(
      (1 / ratio.getLimitPrice()) * givenSuppliedValue.value.toNumber()
    );
    const contributionOutcome = { poolShare, ratio, givenSuppliedValue, givenGatheredValue };

    setLpTokens(lpTokens);
    setContributionOutcome(contributionOutcome);
  }, [activeTasteTest, given, latestHistoryRecord]);

  return (
    <TasteTestDetailContext.Provider
      value={{
        actionType,
        activeTasteTest,
        contributionOutcome,
        isLoading: isLoading || isLoadingTasteTestPersonalization || isLoadingTasteTestHistory,
        isTasteTestClosing,
        isTasteTestDraft,
        isTasteTestGeofenced,
        isTasteTestInactive,
        isTasteTestModerated,
        isTasteTestPending,
        isTasteTestReadOnly,
        latestHistoryRecord,
        lpTokens,
        persistedContributionsHistory,
        removePersistedContributionsHistory,
        resetTasteTestState,
        setActionType,
        setShowCurrentPosition,
        showCurrentPosition,
        tasteTestPersonalization,
        updatePersistedContributionsHistory,
        withCurrentPosition,
      }}
    >
      {children}
    </TasteTestDetailContext.Provider>
  );
};
